import React from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Typography,
  Alert,
  Spin,
} from 'antd';
import { PlusOutlined, EditOutlined, UserOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuthors } from '../Hooks/Courses/useAuthor';

const { Text } = Typography;
const { TextArea } = Input;

const AuthorList = () => {
  const navigate = useNavigate();
  const {
    authors,
    error,
    loading,
    modalVisible,
    editingAuthor,
    form,
    pagination,
    handleSubmit,
    handleEdit,
    handleModalClose,
    handleAddNew,
    handlePageChange
  } = useAuthors();

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name) => <Text strong>{name}</Text>,
      responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      responsive: ['sm', 'md', 'lg', 'xl'],
    },
    {
      title: 'LinkedIn Profile',
      dataIndex: 'linkedInProfile',
      key: 'linkedInProfile',
      render: (url) => (
        <a href={url} target="_blank" rel="noopener noreferrer">
          {url}
        </a>
      ),
      responsive: ['md', 'lg', 'xl'],
    },
    {
      title: 'Bio',
      dataIndex: 'bio',
      key: 'bio',
      render: (bio) => (
        <Text type="secondary">
          {bio && bio.length > 100
            ? `${bio.substring(0, 100)}...`
            : bio || 'No bio provided'}
        </Text>
      ),
      responsive: ['lg', 'xl'],
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEdit(record)}
        >
          Edit
        </Button>
      ),
      responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
    },
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error Loading Authors"
        description={error}
        type="error"
        showIcon
        style={{ margin: "20px" }}
        action={
          <Button size="small" onClick={() => navigate(-1)}>
            Go Back
          </Button>
        }
      />
    );
  }

  if (!authors || authors.length === 0) {
    return (
      <Alert
        message="Authors Not Found"
        description="Start by adding your first author."
        type="warning"
        showIcon
        style={{ margin: "20px" }}
        action={
          <Button size="small" onClick={handleAddNew}>
            Add Author
          </Button>
        }
      />
    );
  }

  return (
    <Card
      className="card-professional"
      title={
        <div>
          <UserOutlined /> Author Management
        </div>
      }
      extra={
        <Space wrap>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddNew}
          >
            Add Author
          </Button>
          <Button
            type="default"
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
        </Space>
      }
      styles={{ body: { padding: 0 } }}
    >
      <div style={{ overflowX: 'auto' }}>
        <Table
          columns={columns}
          dataSource={authors}
          loading={loading}
          rowKey="id"
          scroll={{ x: "max-content" }}
          pagination={{
            current: pagination.currentPage,
            pageSize: pagination.pageSize,
              pageSizeOptions: ['10', '15', '20', '30'], 

            total: pagination.totalCount,
            showSizeChanger: true,
            onChange: handlePageChange,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} authors`
          }}
        />
      </div>

      {/* Add/Edit Author Modal */}
      <Modal
        title={editingAuthor ? 'Edit Author' : 'Add New Author'}
        open={modalVisible}
        onCancel={handleModalClose}
        footer={null}
        destroyOnClose
        width="90%"
        style={{ maxWidth: 600 }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
        >
          <Form.Item
            name="name"
            label="Author Name"
            rules={[
              { required: true, message: 'Please enter author name' },
              { min: 2, message: 'Name must be at least 2 characters' },
              { max: 100, message: 'Name cannot exceed 100 characters' }
            ]}
          >
            <Input placeholder="Enter author name" />
          </Form.Item>

          <Form.Item
            name="email"
            label="Author Email"
            rules={[
              { required: true, message: 'Please enter author email' },
              { type: 'email', message: 'Please enter a valid email' }
            ]}
          >
            <Input placeholder="Enter author email" />
          </Form.Item>

          <Form.Item
            name="linkedInProfile"
            label="LinkedIn Profile"
            rules={[
              { required: true, message: 'Please enter LinkedIn profile URL' },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  const linkedInRegex = /^https:\/\/(www\.)?linkedin\.com\/in\/[a-zA-Z0-9-]+\/?$/;
                  return linkedInRegex.test(value)
                    ? Promise.resolve()
                    : Promise.reject(new Error('Please enter a valid LinkedIn profile URL (e.g., https://www.linkedin.com/in/username)'));
                }
              }
            ]}
          >
            <Input placeholder="https://www.linkedin.com/in/username" />
          </Form.Item>

          <Form.Item
            name="bio"
            label="Author Bio"
            rules={[{ max: 500, message: 'Bio cannot exceed 500 characters' }]}
          >
            <TextArea
              placeholder="Enter author bio"
              rows={4}
              showCount
              maxLength={500}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space wrap>
              <Button onClick={handleModalClose}>Cancel</Button>
              <Button type="primary" htmlType="submit">
                {editingAuthor ? 'Update Author' : 'Add Author'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default AuthorList;
